import type { PlainMessage, Timestamp } from "@bufbuild/protobuf";
import { useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { QRCodeCanvas } from "qrcode.react";
import { useCallback, useEffect, useState } from "react";
import { useInterval } from "usehooks-ts";
import Refresh from "~icons/material-symbols/refresh";
import { css } from "../../../../styled-system/css";
import Header from "../../../components/header";
import { AppError, ERROR_CODES } from "../../../libs/errors";
import { t } from "../../../libs/i18n";
import { getServiceURL } from "../../../libs/ps";
import { calculateRemainingTime } from "../../../libs/timer";
import { formatNumber } from "../../../libs/utils";

type Search = {
  from?: string;
};

export const Route = createFileRoute(
  "/_authed/transfer/qr/$fundSourceId/$amount",
)({
  validateSearch: (search: Search) => {
    return search;
  },
  beforeLoad: ({ context: { tenant } }) => {
    if (!tenant.feature.transfer) {
      throw redirect({ to: "/" });
    }
  },
  loader: async ({
    context: { tenant, client },
    params: { fundSourceId, amount: amountParam },
  }) => {
    const balanceResponse = await client.getMyBalances({});
    const { balanceWithFundSources } = balanceResponse;
    const targetBalances = balanceWithFundSources.filter(
      (b) =>
        b?.userBalance?.amount &&
        b.userBalance.amount > 0 &&
        b.fundSource?.id === fundSourceId,
    );

    const targetBalance = targetBalances[0];
    const targetFundSource = targetBalance.fundSource;

    if (!targetFundSource) {
      throw new Error("targetFundSource not found");
    }

    if (targetFundSource.forbidTransfer) {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
        t("譲渡できないポイントが選択されました"),
        { skipSentry: true },
      );
    }

    const amount = Number(amountParam);

    return {
      amount,
      targetFundSource,
      tenant,
    };
  },
  component: RouteComponent,
});

// 残り時間を表示するコンポーネント
function RemainingTimeDisplay({
  expiresAt,
  onRefresh,
}: {
  expiresAt: PlainMessage<Timestamp> | undefined;
  onRefresh: () => void;
}) {
  const [remainingTime, setRemainingTime] = useState("");

  const updateRemainingTime = useCallback(() => {
    if (!expiresAt) {
      setRemainingTime("00:00");
      return;
    }

    setRemainingTime(calculateRemainingTime(expiresAt));
  }, [expiresAt]);

  useEffect(() => {
    updateRemainingTime();
  }, [updateRemainingTime]);

  // 1秒ごとに更新
  useInterval(updateRemainingTime, 1000);

  return (
    <button
      type="button"
      className={css({
        color: "text.tertiary",
        px: "6px",
        mx: "auto",
        rounded: "md",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        fontSize: "14px",
      })}
      onClick={onRefresh}
    >
      <div>{remainingTime}</div>
      <Refresh
        className={css({
          width: "16px",
          height: "16px",
        })}
      />
    </button>
  );
}

const loading = {
  animation: "spin 1s linear infinite",
};

const loadingData = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1.2em' height='1.2em' viewBox='0 0 24 24'%3E%3Cdefs%3E%3ClinearGradient id='mingcuteLoadingLine0' x1='50%25' x2='50%25' y1='5.271%25' y2='91.793%25'%3E%3Cstop offset='0%25' stop-color='currentColor'/%3E%3Cstop offset='100%25' stop-color='currentColor' stop-opacity='.55'/%3E%3C/linearGradient%3E%3ClinearGradient id='mingcuteLoadingLine1' x1='50%25' x2='50%25' y1='8.877%25' y2='90.415%25'%3E%3Cstop offset='0%25' stop-color='currentColor' stop-opacity='0'/%3E%3Cstop offset='100%25' stop-color='currentColor' stop-opacity='.55'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cg fill='none'%3E%3Cpath d='m12.593 23.258l-.011.002l-.071.035l-.02.004l-.014-.004l-.071-.035q-.016-.005-.024.005l-.004.01l-.017.428l.005.02l.01.013l.104.074l.015.004l.012-.004l.104-.074l.012-.016l.004-.017l-.017-.427q-.004-.016-.017-.018m.265-.113l-.013.002l-.185.093l-.01.01l-.003.011l.018.43l.005.012l.008.007l.201.093q.019.005.029-.008l.004-.014l-.034-.614q-.005-.018-.02-.022m-.715.002a.02.02 0 0 0-.027.006l-.006.014l-.034.614q.001.018.017.024l.015-.002l.201-.093l.01-.008l.004-.011l.017-.43l-.003-.012l-.01-.01z'/%3E%3Cpath fill='url(%23mingcuteLoadingLine0)' d='M8.886.006a1 1 0 0 1 .22 1.988A8.001 8.001 0 0 0 10 17.944v2c-5.523 0-10-4.476-10-10C0 4.838 3.848.566 8.886.007Z' transform='translate(2 2.055)'/%3E%3Cpath fill='url(%23mingcuteLoadingLine1)' d='M14.322 1.985a1 1 0 0 1 1.392-.248A9.99 9.99 0 0 1 20 9.945c0 5.523-4.477 10-10 10v-2a8 8 0 0 0 4.57-14.567a1 1 0 0 1-.248-1.393' transform='translate(2 2.055)'/%3E%3C/g%3E%3C/svg%3E`;

function RouteComponent() {
  const navigate = useNavigate();
  const { from } = Route.useSearch();
  const { targetFundSource, amount, tenant } = Route.useLoaderData();
  const { client } = Route.useRouteContext();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);
  const [expiresAt, setExpiresAt] = useState<
    PlainMessage<Timestamp> | undefined
  >(undefined);
  const [acceptUrl, setAcceptUrl] = useState<string>("");

  const fetchTransferCode = useCallback(async () => {
    try {
      setIsLoading(true);

      const transferCodeResponse = await client
        .upsertTransferCode({
          fundSourceId: targetFundSource.id,
          amount: BigInt(amount),
        })
        .catch((error) => {
          throw new AppError(
            ERROR_CODES.CONNECT_UPSERT_TRANSFER_CODE_FAILED,
            error.message,
            {
              originalError: error,
            },
          );
        });

      const { transferCode, expiresAt } = transferCodeResponse;
      const transferCodePath = `/transfer/accept/${transferCode}?a=${amount}`;
      const newAcceptUrl = getServiceURL(tenant.serviceId, transferCodePath);

      setExpiresAt(expiresAt);
      setAcceptUrl(newAcceptUrl);
    } catch (error) {
      console.error("Error fetching transfer code:", error);
    } finally {
      setIsLoading(false);
    }
  }, [amount, targetFundSource.id, tenant.serviceId, client]);

  useEffect(() => {
    fetchTransferCode();
  }, [fetchTransferCode]);

  // ページを離れるときに、取引履歴のキャッシュを無効化する
  useEffect(() => {
    queryClient.resetQueries({ queryKey: ["transactions"] });

    return () => {
      queryClient.resetQueries({ queryKey: ["transactions"] });
    };
  }, [queryClient]);

  const handleRefresh = useCallback(() => {
    fetchTransferCode();
  }, [fetchTransferCode]);

  return (
    <>
      <Header
        title={t("ポイントを送る")}
        onClickBack={() =>
          navigate({
            to: "/transfer/input/$fundSourceId/$amount",
            params: {
              fundSourceId: targetFundSource.id,
              amount: amount.toString(),
            },
            search: { from },
          })
        }
      />
      <div
        className={css({
          padding: "16px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
        })}
      >
        <div
          className={css({
            bg: "white",
            borderRadius: "18px",
            p: "16px",
            textAlign: "center",
          })}
        >
          <h3
            className={css({
              fontWeight: "bold",
            })}
          >
            {targetFundSource.name}
          </h3>
          <div
            className={css({
              display: "flex",
              flexDir: "column",
              gap: "8px",
            })}
          >
            <div
              className={css({
                fontSize: "16px",
                color: "surface.accentSuccess",
                bg: "surface.accentSuccessLight",
                rounded: "full",
                px: "12px",
                py: "2px",
                mx: "auto",
                mt: "8px",
              })}
            >
              {formatNumber(amount)} pt
            </div>
          </div>

          <div
            className={css({
              display: "flex",
              justifyContent: "center",
              mt: "16px",
            })}
          >
            {isLoading ? (
              <div
                className={css({
                  width: "140px",
                  height: "140px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                })}
              >
                <img
                  src={loadingData}
                  alt="loading"
                  className={css({ ...loading })}
                />
              </div>
            ) : (
              <QRCodeCanvas value={acceptUrl} size={140} />
            )}
          </div>

          <div
            className={css({
              width: "100%",
              mt: "8px",
            })}
          >
            <RemainingTimeDisplay
              expiresAt={expiresAt}
              onRefresh={handleRefresh}
            />
          </div>

          <div
            className={css({
              fontSize: "16px",
              color: "text.primary",
              mt: "16px",
            })}
          >
            {t("受け取り先アカウントから")}
            <br />
            {t("QRコードを読み込んでください")}
          </div>
        </div>

        <Link
          replace
          to="/"
          className={css({
            bg: "rgba(229, 231, 235, 0.5)",
            border: "1px solid",
            borderColor: "surface.secondary",
            borderRadius: "16px",
            p: "12px",
            textAlign: "center",
            mt: "8px",
          })}
        >
          {t("閉じる")}
        </Link>
      </div>
    </>
  );
}
